import { ECoworkingOfferType, EJsonQueryReadyBusinessType, EOfficeType } from '../api-models/common/json_query';
import { infrastructureItemToJsonQueryFieldMap } from './constants/infrastructureFieldMap';
import { getTermsValue, setTerms } from './helpers';
import { TJsonQuery, TJsonQueryOptionalKeys } from './types';

/**
 * Поля коммерческих advanced-фильтров, не используемые в CommercialLand.
 * Нужны для очистки jsonQuery при переключении на CommercialLand из других категорий —
 * иначе старые значения (этаж, состояние, тип здания и т.п.) будут уходить в API,
 * хотя UI их не показывает.
 *
 * Whitelist (поля которые остаются): contract, commission_type, electronic_trading,
 * cl (без агентам не звонить — nested в commission), publication_period, has_photo,
 * has_video, contain_words, exclude_words, contact, id, region, total_area, price,
 * is_by_commercial_owner.
 */
export const COMMERCIAL_LAND_RESET_KEYS: TJsonQueryOptionalKeys[] = [
  'floor',
  'floor_types',
  'floorn',
  'parking_type',
  'condition_type',
  'condition_type__free_appointment_object',
  'condition_type__industry',
  'building_heating_type',
  'building_class_type',
  'building_class_type__warehouse',
  'building_type__building',
  'building_type2',
  'house_year',
  'has_furniture',
  'enter',
  'only_foot',
  'foot_min',
  'placement_type',
  'building_house_line_type__shopping_area',
  'building_lift_types_type',
  'building_cranage_types_type',
  'building_gates_type',
  'input_type',
  'specialty_types',
  'amenity',
  'coworking_id',
  'all_week_access',
  'all_day_access',
  'estate_type',
  'workplace_count',
  ...(Object.values(infrastructureItemToJsonQueryFieldMap) as TJsonQueryOptionalKeys[]),
];

/**
 * Поля, которые нужно сбрасывать при выходе из commercial offerType в non-commercial
 * (residential / suburban / garage).
 *
 * `unsetUnavailableProperties` (через availability) подчищает большинство commercial-only
 * полей, но shared-поля (`total_area`, `floor`, `floorn`, `parking_type`, `house_year`,
 * `has_furniture`, `only_foot`, `foot_min`) остаются — у них availability покрывает и
 * residential. Из-за этого «Общая площадь от 3000 м²» залипает тегом после переключения
 * Снять офис → Снять квартиру.
 */
export const COMMERCIAL_EXIT_RESET_KEYS: TJsonQueryOptionalKeys[] = [...COMMERCIAL_LAND_RESET_KEYS, 'total_area'];

export function appendOfficeType(jsonQuery: TJsonQuery) {
  return (officeType: EOfficeType): TJsonQuery => {
    const officeTypes: EOfficeType[] = getTermsValue('office_type')(jsonQuery) || [];

    if (officeTypes.includes(officeType)) {
      return jsonQuery;
    }

    return setTerms('office_type')(jsonQuery)(officeTypes.concat(officeType));
  };
}

export function appendReadyBusinessType(jsonQuery: TJsonQuery) {
  return (type: EJsonQueryReadyBusinessType): TJsonQuery => {
    const types: EJsonQueryReadyBusinessType[] = getTermsValue('ready_business_types')(jsonQuery) || [];

    if (types.includes(type)) {
      return jsonQuery;
    }

    return setTerms('ready_business_types')(jsonQuery)(types.concat(type));
  };
}

export function appendCoworkingOfferType(jsonQuery: TJsonQuery) {
  return (type: ECoworkingOfferType): TJsonQuery => {
    const types: ECoworkingOfferType[] = getTermsValue('coworking_offer_type')(jsonQuery) || [];

    if (types.includes(type)) {
      return jsonQuery;
    }

    return setTerms('coworking_offer_type')(jsonQuery)(types.concat(type));
  };
}
